import React, { useState, useEffect } from 'react'
import {
  Modal, Form, InputNumber, Radio, Row, Col, Button, Tooltip, Checkbox, Divider,
} from 'antd'
import VoiceTextarea from './VoiceTextarea'
import { ShoppingCartOutlined, SafetyOutlined } from '@ant-design/icons'
import RdaInterventoModal from './RdaInterventoModal'
import type { WorkOrder } from '@/types'

export interface NuovoInterventoData {
  descrizione_intervento: string
  modalita_risoluzione_id: number   // 1=riparazione, 2=sostituzione(acquisto)
  tipo_intervento: 'Meccanico' | 'Carrozziere'
  durata_stimata: number
  note_intervento: string | null
  ore_effettive?: number | null
  coperto_sinistro?: boolean
}

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (data: NuovoInterventoData) => void
  /** Se fornito, la modale si apre in modalità modifica */
  initialData?: NuovoInterventoData
  /** ID intervento già salvato — abilita il bottone "Gestisci RDA" */
  interventionId?: number
  workOrderId?: number
  /** Work order corrente — usato per mostrare opzione sinistro */
  workOrder?: WorkOrder
  /**
   * Copertura sinistro modificabile: richiede work_orders.edit
   * (amministrazione e direzione). Default true per non cambiare il
   * comportamento dei chiamanti che non la passano.
   * In creazione il campo resta comunque editabile: non c'è ancora un
   * valore da proteggere.
   */
  canEditSinistro?: boolean
}

const NuovoInterventoModal: React.FC<Props> = ({ open, onClose, onConfirm, initialData, interventionId, workOrderId, workOrder, canEditSinistro = true }) => {
  const [form] = Form.useForm()
  const [rdaModalOpen, setRdaModalOpen] = useState(false)

  const isEditing = !!initialData
  const hasSinistro = !!workOrder?.sinistro

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setRdaModalOpen(false)
      return
    }

    form.resetFields()
    if (isEditing) {
      form.setFieldsValue({
        descrizione_intervento: initialData.descrizione_intervento,
        modalita_risoluzione_id: initialData.modalita_risoluzione_id ?? 1,
        tipo_intervento: initialData.tipo_intervento,
        durata_stimata: initialData.durata_stimata,
        note_intervento: initialData.note_intervento ?? undefined,
        ore_effettive: initialData.ore_effettive ?? undefined,
        coperto_sinistro: initialData.coperto_sinistro ?? false,
      })
    } else {
      form.setFieldsValue({ modalita_risoluzione_id: 1, tipo_intervento: 'Meccanico', coperto_sinistro: false })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      onConfirm({
        descrizione_intervento: values.descrizione_intervento,
        modalita_risoluzione_id: values.modalita_risoluzione_id,
        tipo_intervento: values.tipo_intervento,
        durata_stimata: values.durata_stimata ?? 0,
        note_intervento: values.note_intervento ?? null,
        coperto_sinistro: values.coperto_sinistro ?? false,
        ...(isEditing && { ore_effettive: values.ore_effettive ?? null }),
      })
    } catch {
      // validation errors shown inline
    }
  }

  return (
    <Modal
      title={isEditing ? 'Modifica Intervento' : 'Nuovo Intervento'}
      open={open}
      onCancel={onClose}
      width="min(600px, 96vw)"
      footer={[
        <div key="rda-btn" style={{ float: 'left' }}>
          <Tooltip title={!interventionId ? "Salva prima l'intervento per gestire la RDA" : undefined}>
            <Button
              icon={<ShoppingCartOutlined />}
              disabled={!interventionId}
              onClick={() => setRdaModalOpen(true)}
            >
              Gestisci RDA
            </Button>
          </Tooltip>
        </div>,
        <Button key="cancel" onClick={onClose}>Annulla</Button>,
        <Button key="ok" type="primary" onClick={handleOk}>Salva</Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark="optional">

        {/* Riga 1: Descrizione + Riparazione/Sostituzione */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 4 }}>
          <Form.Item
            name="descrizione_intervento"
            label="Descrizione parte"
            rules={[{ required: true, message: 'Inserisci la descrizione della parte' }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <VoiceTextarea rows={3} minHeight={90} maxHeight={200} placeholder="Es: paraurti anteriore destra, motore, portiera posteriore sinistra…" label="Descrizione parte" compact />
          </Form.Item>

          <Form.Item
            name="modalita_risoluzione_id"
            label="Tipo"
            rules={[{ required: true }]}
            style={{ flexShrink: 0, marginBottom: 0 }}
          >
            <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 2 }}>
              <Radio value={1}>Riparazione</Radio>
              <Radio value={2}>Sostituzione</Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        {/* Riga 2: Tipo intervento + Ore stimate [+ Ore effettive in edit] */}
        <Row gutter={12} style={{ marginTop: 12 }}>
          <Col span={isEditing ? 8 : 12}>
            <Form.Item
              name="tipo_intervento"
              label="Reparto"
              rules={[{ required: true, message: 'Seleziona il reparto' }]}
              style={{ marginBottom: 8 }}
            >
              <Radio.Group>
                <Radio.Button value="Meccanico">Meccanico</Radio.Button>
                <Radio.Button value="Carrozziere">Carrozziere</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={isEditing ? 8 : 12}>
            <Form.Item
              name="durata_stimata"
              label="Ore stimate"
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                precision={2}
                controls={false}
                style={{ width: '100%' }}
                placeholder="0.00"
                addonAfter="h"
              />
            </Form.Item>
          </Col>

          {isEditing && (
            <Col span={8}>
              <Form.Item
                name="ore_effettive"
                label="Ore effettive"
                style={{ marginBottom: 8 }}
              >
                <InputNumber
                  min={0}
                  max={1000}
                  step={0.5}
                  precision={2}
                  controls={false}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  addonAfter="h"
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Note */}
        <Form.Item name="note_intervento" label="Note" style={{ marginBottom: hasSinistro ? 8 : 0 }}>
          <VoiceTextarea rows={2} minHeight={70} maxHeight={150} placeholder="Note aggiuntive sull'intervento (opzionale)" label="Note intervento" compact />
        </Form.Item>

        {/* Sinistro — visibile solo se la WO ha un sinistro registrato */}
        {hasSinistro && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            {isEditing && !canEditSinistro ? (
              <>
                {/* Sola lettura: cambiare la copertura richiede work_orders.edit.
                    Il valore resta registrato nel form (Form.Item hidden) così il
                    PUT rimanda quello attuale e il backend lo vede invariato. */}
                <Form.Item name="coperto_sinistro" valuePropName="checked" hidden>
                  <Checkbox />
                </Form.Item>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <SafetyOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                  <span>
                    {initialData?.coperto_sinistro
                      ? 'Intervento coperto dal sinistro'
                      : 'Intervento non coperto dal sinistro'}
                  </span>
                  {workOrder?.numero_sinistro && (
                    <span style={{ color: '#8c8c8c', marginLeft: 8, fontSize: 12 }}>
                      (N° {workOrder.numero_sinistro})
                    </span>
                  )}
                </div>
              </>
            ) : (
              <Form.Item name="coperto_sinistro" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>
                  <SafetyOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                  Intervento coperto da sinistro
                  {workOrder?.numero_sinistro && (
                    <span style={{ color: '#8c8c8c', marginLeft: 8, fontSize: 12 }}>
                      (N° {workOrder.numero_sinistro})
                    </span>
                  )}
                </Checkbox>
              </Form.Item>
            )}
          </>
        )}

      </Form>

      {interventionId && workOrderId && (
        <RdaInterventoModal
          open={rdaModalOpen}
          onClose={() => setRdaModalOpen(false)}
          interventionId={interventionId}
          workOrderId={workOrderId}
          interventionInfo={{
            descrizione: form.getFieldValue('descrizione_intervento') ?? initialData?.descrizione_intervento ?? '',
            note: form.getFieldValue('note_intervento') ?? initialData?.note_intervento,
          }}
        />
      )}
    </Modal>
  )
}

export default NuovoInterventoModal
